/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import { ReviewModalShell } from "../record-review/ReviewModalShell";
import {
  useAccountSummary,
  AccountSummaryIds,
} from "./helpers/useAccountSummary";
import { AccountSummaryCard } from "./AccountSummaryCard";

/*
 * The account summary as a modal, standing in for the contact-shaped modals
 * on ACCOUNT rows and for a full-page navigation from navbar search.
 *
 * The card was built to move here - it takes a summary and optional hosting
 * callbacks - so everything below is hosting: the data, the busy guard the
 * shell enforces, and the queue navigation the grid owns. The shell itself is
 * the record modal's, shared rather than restated.
 */
export const AccountSummaryModal = (
  props: AccountSummaryIds & {
    open: boolean;
    onClose: () => void;
    onPreviousRowRequested?: () => void;
    onNextRowRequested?: () => void;
    /** The suggested-change ids that are no longer pending, for the grid. */
    onSuccess: (decidedChangeIds?: number[]) => void;
  }
) => {
  // Nothing is fetched for a closed modal; opening it starts the load.
  const { summary, failed, reload, reloadSilently } = useAccountSummary(
    props,
    props.open
  );
  /* Owned here and handed down: the card's own Close goes quiet from it. */
  const [busy, setBusy] = useState(false);

  return (
    <ReviewModalShell
      open={props.open}
      onClose={props.onClose}
      busy={busy}
      failed={failed}
      failureMessage="This account could not be loaded. The request may have failed, in which case trying again will recover it."
      onRetry={reload}
      loadingTitle="Fetching account data"
    >
      {summary && (
        <AccountSummaryCard
          summary={summary}
          anchorChangeId={props.suggestedChangeId}
          onClose={props.onClose}
          onPreviousRowRequested={props.onPreviousRowRequested}
          onNextRowRequested={props.onNextRowRequested}
          busy={busy}
          onBusyChange={setBusy}
          onChanged={(decidedChangeIds) => {
            reloadSilently();
            props.onSuccess(decidedChangeIds);
          }}
        />
      )}
    </ReviewModalShell>
  );
};
